import { useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

// Service-level monthly targets: target vs actual units per service type.
export const ServiceTargets = ({ studioId, rows = [], overall = 0, canEdit, onSaved }) => {
  const [edit, setEdit] = useState(null);

  const start = () => {
    const draft = {};
    rows.forEach((r) => {
      draft[r.service] = String(r.target ?? 0);
    });
    setEdit(draft);
  };

  const save = async () => {
    try {
      const service_targets = {};
      Object.entries(edit).forEach(([k, v]) => {
        if (Number(v) > 0) service_targets[k] = Number(v);
      });
      await api.put("/targets", { studio_id: studioId, service_targets });
      toast.success("Service targets saved");
      setEdit(null);
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="service-targets-block">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-600">
          Overall target achievement{" "}
          <span
            data-testid="target-overall-pct"
            className={`font-semibold ${overall >= 70 ? "text-emerald-600" : "text-crimson-600"}`}
          >
            {overall}%
          </span>{" "}
          <span className="text-xs text-slate-500">· average across service types</span>
        </p>
        {canEdit &&
          (edit ? (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="min-h-[44px]"
                onClick={() => setEdit(null)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="min-h-[44px]"
                data-testid="save-service-targets-btn"
                onClick={save}
              >
                Save targets
              </Button>
            </div>
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="min-h-[44px]"
              data-testid="edit-service-targets-btn"
              onClick={start}
            >
              Set targets
            </Button>
          ))}
      </div>

      <div className="overflow-x-auto">
        <Table data-testid="service-targets-table" className="mobile-cards">
          <TableHeader>
            <TableRow>
              <TableHead>Service type</TableHead>
              <TableHead className="text-right">Target units</TableHead>
              <TableHead className="text-right">Actual</TableHead>
              <TableHead className="text-right">Variance</TableHead>
              <TableHead className="text-right">Achieved</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="py-8 text-center text-slate-500">
                  No service targets set for this month yet.
                </TableCell>
              </TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.service} data-testid={`target-row-${r.service}`}>
                <TableCell data-label="Service type" className="font-medium">
                  {r.service}
                </TableCell>
                <TableCell data-label="Target units" className="text-right">
                  {edit ? (
                    <Input
                      className="ml-auto w-24 min-h-[44px] text-right"
                      inputMode="decimal"
                      data-testid={`target-input-${r.service}`}
                      value={edit[r.service] ?? ""}
                      onChange={(e) =>
                        setEdit((d) => ({ ...d, [r.service]: e.target.value }))
                      }
                    />
                  ) : (
                    r.target || "—"
                  )}
                </TableCell>
                <TableCell data-label="Actual" className="text-right font-semibold">
                  {r.actual}
                </TableCell>
                <TableCell
                  data-label="Variance"
                  className={`text-right ${
                    r.variance < 0 ? "text-crimson-600" : "text-emerald-600"
                  }`}
                >
                  {r.variance > 0 ? `+${r.variance}` : r.variance}
                </TableCell>
                <TableCell data-label="Achieved" className="text-right">
                  {r.pct === null ? (
                    <span className="text-xs text-slate-400">no target</span>
                  ) : (
                    <span
                      data-testid={`target-pct-${r.service}`}
                      className={`font-semibold ${
                        r.pct >= 70 ? "text-emerald-600" : "text-crimson-600"
                      }`}
                    >
                      {r.pct}%
                    </span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
